// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./AddPhaseToDesignJobsDialog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// CONTEXT
import { UserContext } from "../../../../context/UserContext";

// API IMPORTS
import { getAPICall, putAPICall } from "../../../../config/apiCalls";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	CircularProgress,
	Collapse,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

// REACT COMPONENT
const AddPhaseToDesignJobsDialog = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// STATES
	const [allPhases, setAllPhases] = useState([]);
	const [selectedPhase, setSelectedPhase] = useState(null);

	// Feedback states
	const [isLoading, setIsLoading] = useState(false);
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");

	// Reset everything and fetch phases when the dialog opens
	useEffect(() => {
		if (props.open) {
			setSelectedPhase(null);
			closeAlert();
			fetchAllPhases();
		}
	}, [props.open]);

	// API FUNCTIONS
	const fetchAllPhases = async () => {
		setIsLoading(true);

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/phases/all"
			);
			setAllPhases(res.data);
		} catch (err) {
			console.error("Error fetching phases:", err);
		}

		setIsLoading(false);
	};

	// Phases that don't already show up in Design Jobs on their own — those
	// with a design phase_type_id, or already flagged in
	const selectablePhases = allPhases.filter(
		(phase) =>
			phase.phase_type_id !== 2 &&
			phase.phase_type_id !== 7 &&
			!phase.include_in_design_jobs
	);

	// INPUT HANDLERS
	const phaseChanged = (event, newValue) => {
		setSelectedPhase(newValue);
	};

	// Feedback handlers
	const openAlert = (message) => {
		setAlertOpen(true);
		setAlertMessage(message);
	};

	const closeAlert = () => {
		setAlertOpen(false);
		setAlertMessage("");
	};

	// SUBMIT
	const submitButtonClicked = async () => {
		if (!selectedPhase) {
			openAlert("Please select a phase.");
			return;
		}

		setIsLoading(true);

		try {
			await putAPICall(
				instance,
				accounts[0],
				"/api/jobs/design/toggleInclude",
				{
					id: selectedPhase.id,
					include: 1,
					employeeID: employeeID,
				}
			);

			props.fetchDesignJobsRecords();
			formClosed();
		} catch (err) {
			console.error("Error adding phase to Design Jobs:", err);
			openAlert("An error occurred. Please try again.");
		}

		setIsLoading(false);
	};

	const formClosed = () => {
		props.addPhaseToDesignJobsDialogClosed();
	};

	// RENDER
	return (
		<Dialog
			id="addPhaseToDesignJobsDialog"
			open={props.open}
			fullWidth
			maxWidth="sm"
			onClose={formClosed}
		>
			<DialogTitle id="addPhaseToDesignJobsDialogTitle">
				Add Phase to Design Jobs
			</DialogTitle>
			<DialogContent id="addPhaseToDesignJobsDialogContent">
				<div className="addPhaseToDesignJobsDialogRow">
					<Collapse sx={{ flex: 1 }} in={alertOpen}>
						<Alert
							severity="error"
							action={
								<IconButton
									aria-label="close"
									color="inherit"
									size="small"
									onClick={closeAlert}
								>
									<CloseIcon fontSize="inherit" />
								</IconButton>
							}
						>
							{alertMessage}
						</Alert>
					</Collapse>
				</div>
				{isLoading && (
					<div className="loadingSpinnerContainer">
						<CircularProgress color="primary" />
					</div>
				)}
				{!isLoading && (
					<div className="addPhaseToDesignJobsDialogRow">
						<Autocomplete
							sx={{ flex: 1 }}
							id="addPhaseToDesignJobsSelect"
							value={selectedPhase}
							onChange={phaseChanged}
							options={selectablePhases}
							getOptionLabel={(option) =>
								`${option.property_name}: ${option.project_name}: ${option.name} (${option.phase_type_name})`
							}
							renderInput={(params) => (
								<TextField {...params} label="Phase" placeholder="Phase" />
							)}
						/>
					</div>
				)}
				<div className="dialogButtons">
					<Button onClick={formClosed} variant="outlined" color="error">
						Cancel
					</Button>
					<Button onClick={submitButtonClicked} variant="contained">
						Submit
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

// EXPORT
export default AddPhaseToDesignJobsDialog;
